import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { Subscription, filter, firstValueFrom } from 'rxjs';
import { DEFAULT_RISK_PROFILE, RiskStep } from './models/risk-setup';
import { TradeEntry } from './models/trade-entry';
import { ApiService } from './services/api.service';
import { AuthService } from './services/auth.service';
import { RiskBudgetService } from './services/risk-budget.service';
import { SessionTimerService } from './services/session-timer.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Tradingtagebuch';
  protected readonly location = window.location;
  stageNumber = 1;
  lossStreak = 0;
  openPositionCount = 0;
  totalTradesCount = 0;
  menuOpen = false;
  sessionRemainingLabel = '60:00';
  private subscriptions = new Subscription();

  constructor(
      private riskBudget: RiskBudgetService,
      private api: ApiService,
      private auth: AuthService,
      private router: Router,
      private sessionTimer: SessionTimerService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
        this.riskBudget.summary$.subscribe((summary) => {
          this.stageNumber = this.computeStageNumber(summary.profile.riskSteps, summary.positionedPnLEUR);
        })
    );
    this.subscriptions.add(
        this.auth.loggedIn$.subscribe((loggedIn: boolean) => {
          if (loggedIn) {
            void this.loadHeaderStats();
            void this.loadRiskProfileData();
            this.sessionTimer.start();
          } else {
            this.lossStreak = 0;
            this.openPositionCount = 0;
            this.totalTradesCount = 0;
            this.menuOpen = false;
            this.sessionTimer.stop();
          }
        })
    );
    this.subscriptions.add(
        this.api.tradeChanges$.subscribe(() => {
          if (!this.isAuthenticated) return;
          void this.loadHeaderStats();
        })
    );
    this.subscriptions.add(
        this.router.events
          .pipe(filter((event) => event instanceof NavigationEnd))
          .subscribe(() => {
            if (!this.isAuthenticated) return;
            this.sessionTimer.recordActivity();
          })
    );
    this.subscriptions.add(
        this.sessionTimer.remainingMs$.subscribe((remainingMs) => {
          this.sessionRemainingLabel = this.formatRemainingTime(remainingMs);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isAuthenticated(): boolean {
    return this.auth.isLoggedIn();
  }

  logout(): void {
    this.auth.logout();
    this.menuOpen = false;
    this.router.navigateByUrl('/login');
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  openPositionBadgeClass(): string {
    if (this.openPositionCount < 5) {
      return 'header-stage__badge--ok';
    }
    if (this.openPositionCount < 8) {
      return 'header-stage__badge--warning';
    }
    return 'header-stage__badge--alert';
  }

  totalTradesBadgeIcon(): string {
    if (this.totalTradesCount >= 200) {
      return '🏅';
    }
    if (this.totalTradesCount >= 100) {
      return '🛡️';
    }
    if (this.totalTradesCount >= 50) {
      return '⭐';
    }
    return '';
  }

  private computeStageNumber(steps: RiskStep[] | undefined, positionedPnL: number): number {
    const normalized = (steps ?? DEFAULT_RISK_PROFILE.riskSteps ?? [])
        .map((step) => ({ profitMinEUR: Number(step.profitMinEUR ?? 0) }))
        .filter((step) => !Number.isNaN(step.profitMinEUR))
        .sort((a, b) => a.profitMinEUR - b.profitMinEUR);

    if (!normalized.length) {
      return 1;
    }

    const profit = Math.max(0, positionedPnL ?? 0);
    let stageIndex = 0;
    normalized.forEach((step, index) => {
      if (profit >= step.profitMinEUR) {
        stageIndex = index;
      }
    });

    return stageIndex + 1;
  }

  private async loadHeaderStats(): Promise<void> {
    try {
      const trades = await firstValueFrom(this.api.getTrades());
      const normalized = trades ?? [];
      this.lossStreak = this.calculateLossStreak(normalized);
      this.openPositionCount = normalized.filter((entry) => entry.status === 'Positioniert' || entry.status === 'Teilverkauf').length;
      this.totalTradesCount = normalized.filter((entry) => entry.status === 'Positioniert' || entry.status === 'Glatt').length;
    } catch {
      this.lossStreak = 0;
      this.openPositionCount = 0;
      this.totalTradesCount = 0;
    }
  }

  private async loadRiskProfileData(): Promise<void> {
    const currentYear = new Date().getFullYear();
    let planYear = currentYear;

    try {
      const ids = await firstValueFrom(this.api.getRiskProfileIds());
      const years = (ids ?? [])
        .map((id) => Number(id))
        .filter((year) => Number.isFinite(year));

      if (years.length) {
        planYear = years.includes(currentYear) ? currentYear : Math.max(...years);
      }
    } catch {
      // keep current plan year fallback
    }

    try {
      const profile = await firstValueFrom(this.api.getRiskProfile(planYear));
      if (!profile) {
        this.riskBudget.setProfile({ ...DEFAULT_RISK_PROFILE, planYear });
        return;
      }

      try {
        const steps = await firstValueFrom(this.api.getRiskSteps(profile.id ?? String(planYear)));
        if (steps?.length) {
          this.riskBudget.setProfile({ ...profile, riskSteps: steps });
          return;
        }
      } catch {
        // ignore step failures
      }

      this.riskBudget.setProfile(profile);
    } catch {
      this.riskBudget.setProfile({ ...DEFAULT_RISK_PROFILE, planYear });
    }
  }

  private calculateLossStreak(entries: TradeEntry[]): number {
    const realized = [...entries]
      .filter((entry) => this.hasRealizedResult(entry))
      .map((entry) => ({
        entry,
        pnl: this.realizedPnl(entry),
        date: this.realizedDate(entry)
      }))
      .filter((item) => item.date && item.pnl !== 0);

    let streak = 0;
    const dates = [...new Set(realized.map((item) => item.date))]
      .sort((a, b) => b.localeCompare(a));

    for (const date of dates) {
      const dayResults = realized.filter((item) => item.date === date);
      if (dayResults.some((item) => item.pnl > 0)) {
        break;
      }
      streak += dayResults.filter((item) => item.pnl < 0).length;
    }
    return streak;
  }

  private hasRealizedResult(entry: TradeEntry): boolean {
    const status = this.normalizedStatus(entry.status);
    if (status === 'Storniert' || status === 'Watch' || status === 'Positioniert') {
      return false;
    }
    if (entry.partialSales?.length) {
      return this.realizedPnl(entry) !== 0;
    }
    if (entry.sellPrice !== undefined || entry.sellDate || status === 'Glatt') {
      return this.realizedPnl(entry) !== 0;
    }
    return false;
  }

  private normalizedStatus(status: string | undefined): TradeEntry['status'] | undefined {
    const value = (status ?? '').toString().trim().toLowerCase();
    if (value === 'w' || value === 'watch') return 'Watch';
    if (value === 'p' || value === 'positioniert') return 'Positioniert';
    if (value === 't' || value === 'teilverkauf') return 'Teilverkauf';
    if (value === 'g' || value === 'glatt') return 'Glatt';
    if (value === 's' || value === 'storniert') return 'Storniert';
    return undefined;
  }

  private realizedDate(entry: TradeEntry): string {
    const partialDates = (entry.partialSales ?? [])
      .map((sale) => sale.date || '')
      .filter(Boolean)
      .sort();
    return partialDates.at(-1) || entry.sellDate || entry.entryDate || '';
  }

  private realizedPnl(entry: TradeEntry): number {
    if (entry.partialSales?.length) {
      return entry.partialSales.reduce((sum, sale) => sum + sale.pnl, 0);
    }

    const sellPrice = entry.sellPrice;
    const size = entry.sellSize ?? entry.size ?? 0;
    const entryFee = entry.entryFee ?? 0;
    const sellFee = entry.sellFee ?? 0;

    if (sellPrice === undefined || size <= 0) {
      return entry.realizedPnl ?? 0;
    }

    const perShare = (entry.certificate ? 'Long' : entry.direction) === 'Long'
        ? sellPrice - entry.entryPrice
        : entry.entryPrice - sellPrice;

    const total = perShare * size - entryFee - sellFee;
    return +total.toFixed(2);
  }

  private formatRemainingTime(remainingMs: number): string {
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
